import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Brain,
  ChevronDown,
  ChevronUp,
  Loader2,
  Plus,
  TrendingDown,
  TrendingUp,
} from 'lucide-react';
import { toast } from 'sonner';

import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import { Card } from '../../ui/card';

const CATEGORY_OPTIONS = [
  { id: 'all', label: 'All' },
  { id: 'fatherhood', label: 'Dads & Fatherhood' },
  { id: 'marriage', label: 'Marriage & Family' },
  { id: 'faith', label: 'Faith & Christianity' },
  { id: 'career', label: 'Work & Career' },
];

const CATEGORY_LABELS = {
  fatherhood: 'Dads & Fatherhood',
  marriage: 'Marriage & Family',
  faith: 'Faith & Christianity',
  career: 'Work & Career',
};

const formatDate = (value) => {
  if (!value) return 'Never';
  const date = new Date(`${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString(undefined, {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
};

const normalizeThemeKey = (value) => String(value || '').trim().toLowerCase();

const getOpportunityBadge = (score) => {
  if (score >= 8) {
    return {
      label: 'High Gap',
      className: 'border-rose-500/20 bg-rose-500/10 text-rose-700 dark:text-rose-300',
    };
  }
  if (score >= 5) {
    return {
      label: 'Medium Gap',
      className: 'border-amber-500/20 bg-amber-500/10 text-amber-700 dark:text-amber-300',
    };
  }
  return {
    label: 'Being Addressed',
    className: 'border-emerald-500/20 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300',
  };
};

const getTrendMeta = (currentPoint, previousSnapshot) => {
  if (!previousSnapshot) {
    return {
      label: 'New',
      toneClass: 'border-sky-500/20 bg-sky-500/10 text-sky-700 dark:text-sky-300',
      icon: null,
    };
  }

  const previousPoint = (previousSnapshot.pain_points || []).find(
    (item) => normalizeThemeKey(item?.theme) === normalizeThemeKey(currentPoint.theme),
  );

  if (!previousPoint) {
    return {
      label: 'New',
      toneClass: 'border-sky-500/20 bg-sky-500/10 text-sky-700 dark:text-sky-300',
      icon: null,
    };
  }

  const currentFrequency = Number(currentPoint.frequency || 0);
  const previousFrequency = Number(previousPoint.frequency || 0);

  if (currentFrequency > previousFrequency) {
    return {
      label: 'Rising',
      toneClass: 'border-rose-500/20 bg-rose-500/10 text-rose-700 dark:text-rose-300',
      icon: TrendingUp,
    };
  }

  if (currentFrequency < previousFrequency) {
    return {
      label: 'Falling',
      toneClass: 'border-emerald-500/20 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300',
      icon: TrendingDown,
    };
  }

  return {
    label: 'Steady',
    toneClass: 'border-slate-500/20 bg-slate-500/10 text-slate-700 dark:text-slate-300',
    icon: null,
  };
};

export default function MarketingIntelligenceTab({ adminFetch, onAddToQueue }) {
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [snapshots, setSnapshots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [runningScan, setRunningScan] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);

  const fetchSnapshots = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await adminFetch('/admin/marketing/audience-snapshots?weeks=12');
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload?.detail || 'Failed to load audience intelligence.');
      }
      setSnapshots(Array.isArray(payload?.snapshots) ? payload.snapshots : []);
    } catch (fetchError) {
      setError(fetchError?.message || 'Failed to load audience intelligence.');
    } finally {
      setLoading(false);
    }
  }, [adminFetch]);

  useEffect(() => {
    fetchSnapshots();
  }, [fetchSnapshots]);

  const filteredSnapshots = useMemo(() => (
    categoryFilter === 'all'
      ? snapshots
      : snapshots.filter((snapshot) => snapshot.category === categoryFilter)
  ), [categoryFilter, snapshots]);

  const latestReturnedScanDate = useMemo(() => {
    const latestSnapshot = snapshots.find((snapshot) => Boolean(snapshot?.scan_date));
    return latestSnapshot ? String(latestSnapshot.scan_date) : '';
  }, [snapshots]);

  const latestScanDate = useMemo(() => (
    filteredSnapshots.reduce((latest, snapshot) => (
      !latest || String(snapshot.scan_date || '') > latest ? String(snapshot.scan_date || '') : latest
    ), '')
  ), [filteredSnapshots]);

  const currentCards = useMemo(() => {
    if (!filteredSnapshots.length || !latestScanDate) return [];

    const currentSnapshots = filteredSnapshots.filter((snapshot) => snapshot.scan_date === latestScanDate);
    if (!currentSnapshots.length) return [];

    return currentSnapshots
      .flatMap((snapshot) => (
        (snapshot.pain_points || []).map((point) => ({
          ...point,
          category: snapshot.category,
          scan_date: snapshot.scan_date,
          raw_post_count: snapshot.raw_post_count,
        }))
      ))
      .sort((left, right) => {
        const scoreDelta = Number(right.content_opportunity_score || 0) - Number(left.content_opportunity_score || 0);
        if (scoreDelta !== 0) return scoreDelta;
        return Number(right.frequency || 0) - Number(left.frequency || 0);
      })
      .slice(0, 5);
  }, [filteredSnapshots, latestScanDate]);

  const previousSnapshotByCategory = useMemo(() => {
    const lookup = {};
    const categories = categoryFilter === 'all'
      ? Object.keys(CATEGORY_LABELS)
      : [categoryFilter];

    categories.forEach((category) => {
      const categorySnapshots = filteredSnapshots.filter((snapshot) => snapshot.category === category);
      const previous = categorySnapshots.find((snapshot) => snapshot.scan_date !== latestScanDate) || null;
      lookup[category] = previous;
    });

    return lookup;
  }, [categoryFilter, filteredSnapshots, latestScanDate]);

  const historyGroups = useMemo(() => {
    const grouped = new Map();
    filteredSnapshots.forEach((snapshot) => {
      const items = grouped.get(snapshot.scan_date) || [];
      items.push(snapshot);
      grouped.set(snapshot.scan_date, items);
    });
    return Array.from(grouped.entries()).sort((left, right) => String(right[0]).localeCompare(String(left[0])));
  }, [filteredSnapshots]);

  const handleRunScan = async () => {
    setRunningScan(true);
    try {
      const response = await adminFetch('/admin/marketing/run-audience-scan', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(categoryFilter === 'all' ? {} : { category: categoryFilter }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload?.detail || 'Failed to run audience scan.');
      }
      toast.success('Audience scan finished. Refreshing snapshots.');
      await fetchSnapshots();
    } catch (scanError) {
      toast.error(scanError?.message || 'Failed to run audience scan.');
    } finally {
      setRunningScan(false);
    }
  };

  const handleAddToQueue = (theme) => {
    if (typeof onAddToQueue === 'function') {
      onAddToQueue(theme);
      toast.success('Queued this audience theme in Marketing Content.');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-fuchsia-500" />
      </div>
    );
  }

  const hasSnapshots = filteredSnapshots.length > 0;

  return (
    <div className="space-y-6">
      <Card className="border border-fuchsia-200 bg-gradient-to-br from-[#fff5ff] via-white to-[#f6f1ff] p-6 dark:border-fuchsia-500/15 dark:bg-gradient-to-br dark:from-[#1d1420] dark:via-[#17121b] dark:to-[#12101a]">
        <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
          <div className="max-w-3xl">
            <p className="text-[11px] font-semibold uppercase tracking-[0.26em] text-fuchsia-700 dark:text-fuchsia-300">
              Audience Intelligence
            </p>
            <h2 className="mt-3 text-2xl font-semibold text-slate-900 dark:text-white">
              See what dads are aching about before you decide what content to build
            </h2>
            <p className="mt-3 text-sm leading-6 text-slate-600 dark:text-slate-300">
              Weekly YouTube scans surface the pain points showing up most often, how much real advice is being offered,
              and where the biggest content gaps may be right now.
            </p>
          </div>

          <div className="flex flex-col items-start gap-3 xl:items-end">
            <Button
              onClick={handleRunScan}
              type="button"
              variant="outline"
              className="border-fuchsia-200 text-fuchsia-700 hover:bg-fuchsia-50 dark:border-fuchsia-400/20 dark:text-fuchsia-200 dark:hover:bg-fuchsia-400/10"
            >
              {runningScan ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Brain className="mr-2 h-4 w-4" />}
              Run Scan Now
            </Button>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              {latestReturnedScanDate ? `Last scanned: ${formatDate(latestReturnedScanDate)}` : 'Last scanned: Never'}
            </p>
          </div>
        </div>

        <div className="mt-5 flex flex-wrap gap-2">
          {CATEGORY_OPTIONS.map((option) => {
            const isActive = option.id === categoryFilter;
            return (
              <Button
                key={option.id}
                onClick={() => setCategoryFilter(option.id)}
                type="button"
                variant="outline"
                className={isActive
                  ? 'border-fuchsia-300 bg-fuchsia-50 text-fuchsia-800 dark:border-fuchsia-400/40 dark:bg-fuchsia-400/10 dark:text-fuchsia-200'
                  : 'border-slate-200 text-slate-700 hover:bg-slate-100 dark:border-white/10 dark:text-white dark:hover:bg-white/[0.06]'}
              >
                {option.label}
              </Button>
            );
          })}
        </div>
      </Card>

      {error ? (
        <Card className="border-rose-200 bg-rose-50/80 p-6 text-sm text-rose-700 dark:border-rose-500/20 dark:bg-rose-500/10 dark:text-rose-200">
          {error}
        </Card>
      ) : null}

      {!hasSnapshots ? (
        <Card className="border-slate-200 bg-white/90 p-8 text-center dark:border-white/10 dark:bg-white/[0.03]">
          <p className="text-lg font-semibold text-slate-900 dark:text-white">Run your first scan</p>
          <p className="mt-3 text-sm leading-6 text-slate-600 dark:text-slate-400">
            Audience Intelligence has no snapshots yet. Trigger a scan to store the first weekly read on what dads are
            wrestling with right now.
          </p>
          <div className="mt-5">
            <Button onClick={handleRunScan} type="button">
              {runningScan ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Brain className="mr-2 h-4 w-4" />}
              Run Scan Now
            </Button>
          </div>
        </Card>
      ) : (
        <>
          <div className="grid gap-4 xl:grid-cols-2">
            {currentCards.map((point) => {
              const opportunity = getOpportunityBadge(Number(point.content_opportunity_score || 0));
              const trend = getTrendMeta(point, previousSnapshotByCategory[point.category]);
              const TrendIcon = trend.icon;
              return (
                <Card key={`${point.category}-${point.theme}`} className="border-slate-200 bg-white/90 p-6 dark:border-white/10 dark:bg-white/[0.03]">
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge className="border-slate-500/20 bg-slate-500/10 text-slate-700 dark:text-slate-300">
                      {CATEGORY_LABELS[point.category] || point.category}
                    </Badge>
                    <Badge className={opportunity.className}>{opportunity.label}</Badge>
                    <Badge className={trend.toneClass}>
                      {TrendIcon ? <TrendIcon className="mr-1.5 h-3.5 w-3.5" /> : null}
                      {trend.label}
                    </Badge>
                  </div>

                  <h3 className="mt-4 text-xl font-semibold leading-8 text-slate-900 dark:text-white">
                    {point.theme}
                  </h3>

                  <div className="mt-4 grid gap-3 sm:grid-cols-3">
                    <div className="rounded-2xl border border-slate-200 bg-slate-50/80 p-3 dark:border-white/10 dark:bg-white/[0.04]">
                      <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
                        Frequency
                      </p>
                      <p className="mt-2 text-2xl font-semibold text-slate-900 dark:text-white">{point.frequency}</p>
                    </div>
                    <div className="rounded-2xl border border-slate-200 bg-slate-50/80 p-3 dark:border-white/10 dark:bg-white/[0.04]">
                      <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
                        Solutions
                      </p>
                      <p className="mt-2 text-2xl font-semibold capitalize text-slate-900 dark:text-white">{point.solution_density}</p>
                    </div>
                    <div className="rounded-2xl border border-slate-200 bg-slate-50/80 p-3 dark:border-white/10 dark:bg-white/[0.04]">
                      <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
                        Score
                      </p>
                      <p className="mt-2 text-2xl font-semibold text-slate-900 dark:text-white">
                        {point.content_opportunity_score}/10
                      </p>
                    </div>
                  </div>

                  <p className="mt-4 text-sm leading-6 text-slate-600 dark:text-slate-400">
                    Example post: {point.example_post_title || 'No representative title captured'}
                  </p>

                  <div className="mt-5">
                    <Button
                      onClick={() => handleAddToQueue(point.theme)}
                      type="button"
                      className="bg-fuchsia-600 text-white hover:bg-fuchsia-700"
                    >
                      <Plus className="mr-2 h-4 w-4" />
                      Add to Queue
                    </Button>
                  </div>
                </Card>
              );
            })}
          </div>

          <Card className="border-slate-200 bg-white/90 p-6 dark:border-white/10 dark:bg-white/[0.03]">
            <button
              className="flex w-full items-center justify-between text-left"
              onClick={() => setHistoryOpen((current) => !current)}
              type="button"
            >
              <div>
                <p className="text-lg font-semibold text-slate-900 dark:text-white">Weekly History</p>
                <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
                  See which themes are staying persistent and which were one-week spikes.
                </p>
              </div>
              {historyOpen ? <ChevronUp className="h-5 w-5 text-slate-500" /> : <ChevronDown className="h-5 w-5 text-slate-500" />}
            </button>

            {historyOpen ? (
              <div className="mt-6 space-y-4">
                {historyGroups.map(([scanDate, items]) => (
                  <div key={scanDate} className="rounded-2xl border border-slate-200 bg-slate-50/80 p-4 dark:border-white/10 dark:bg-white/[0.04]">
                    <p className="text-sm font-semibold uppercase tracking-[0.18em] text-slate-600 dark:text-slate-300">
                      Week of {formatDate(scanDate)}
                    </p>
                    <div className="mt-3 space-y-3">
                      {items.map((snapshot) => (
                        <div key={`${snapshot.category}-${snapshot.scan_date}`}>
                          <p className="text-sm font-semibold text-slate-900 dark:text-white">
                            {CATEGORY_LABELS[snapshot.category] || snapshot.category}
                          </p>
                          <ul className="mt-2 space-y-1 text-sm text-slate-600 dark:text-slate-400">
                            {(snapshot.pain_points || []).slice(0, 3).map((point) => (
                              <li key={`${snapshot.category}-${snapshot.scan_date}-${point.theme}`}>
                                {point.theme}
                              </li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ) : null}
          </Card>
        </>
      )}
    </div>
  );
}
